import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { useCycleStore } from '@cycle-hangars/store/store';
import { ZodError } from 'zod';
import { emptyServiceLocationDetails, ErrorMessages } from '@types';
import {
  scrollTop,
  parseZodErrors,
  stringFieldSchema,
  textAreaInputSchema,
  queryFeatureLayerById,
  getNearestAddress,
  convertAddressToServiceLocation,
} from '@utils';
import {
  SELECT_OPTIONS_ERROR,
  VALID_ID_ERROR,
} from '@cycle-hangars/constants/errorMessages';
import {
  NavigationButtons,
  RadioOptions,
  TextInputMolecule,
} from '@molecules/*';
import { ErrorSummary } from '@atoms/*';

export const CycleHangarIdDetails = () => {
  const {
    knowsCycleHangarId,
    updateKnowsCycleHangarId,
    cycleHangarId,
    updateCycleHangarId,
    updateServiceLocationDetails,
    isPhotoLocationStored,
    photoCoordinates,
    updateServiceAssetDetails,
  } = useCycleStore();
  const navigate = useNavigate();
  const MAX_LENGTH = 10;
  const [textInput, setTextInput] = useState(cycleHangarId);
  const [result, setResult] = useState(knowsCycleHangarId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const radioOptionTitle = 'Do you know the cycle hangar ID?';
  const radioOptionsDescription =
    'This is usually found on the side or top of the cycle hangar.';
  const textInputLabel = 'Enter cycle hangar ID';
  const textInputDescription =
    'This begins with the letters FP or CH followed by 4 numbers';
  const id = textInput?.toUpperCase().replaceAll(' ', '');
  const handleChangeBinary = (option: string) => {
    setResult(option as 'Yes' | 'No');
    setErrorMessages([]);
  };

  const handleChangeIdInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTextInput(event.target.value);
    setErrorMessages([]);
  };

  const handleIdCheck = async () => {
    try {
      let features = [];
      const cycleHangarsIdCheck = await queryFeatureLayerById(
        'CycleHangars',
        'CH_Ref',
        id,
      );
      if (
        cycleHangarsIdCheck &&
        cycleHangarsIdCheck.data.features.length === 0
      ) {
        const cycleHangarsEstateIdCheck = await queryFeatureLayerById(
          'CycleHangarsEstates',
          'Unit_ID',
          id,
        );
        if (cycleHangarsEstateIdCheck.data.features.length > 0) {
          features = cycleHangarsEstateIdCheck.data.features;
        }
      } else {
        features = cycleHangarsIdCheck.data.features;
      }
      return features;
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  const buttonNext = {
    onClick: async () => {
      try {
        const validatedBinaryData = stringFieldSchema(
          'knowsCycleHangarId',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          knowsCycleHangarId: result,
        });
        if (result === 'Yes') {
          const validateIdInput = textAreaInputSchema({
            fieldName: 'cycleHangarId',
            requiredMessage: VALID_ID_ERROR.required.message,
            maxLength: MAX_LENGTH,
            maxLengthMessage: VALID_ID_ERROR.required.message,
          }).parse({
            cycleHangarId: textInput,
          });
          const idCheck = await handleIdCheck();
          const result = idCheck.length > 0;
          if (!result) {
            setErrorMessages([
              {
                path: 'cycleHangarId',
                message: VALID_ID_ERROR.required.message,
              },
            ]);
            return;
          }
          if (validateIdInput.cycleHangarId) {
            const asset = idCheck[0].geometry.coordinates;
            const latitude = asset[1];
            const longitude = asset[0];

            const address = await getNearestAddress(latitude, longitude);
            if (address !== null) {
              updateServiceLocationDetails(
                convertAddressToServiceLocation(address),
              );
              updateCycleHangarId(validateIdInput.cycleHangarId);
            }
          }
        }
        if (validatedBinaryData.knowsCycleHangarId) {
          updateKnowsCycleHangarId(
            validatedBinaryData.knowsCycleHangarId as 'Yes' | 'No',
          );
        }
        if (result === 'Yes') {
          updateServiceAssetDetails(null);
          navigate(RoutesList.ISSUE_SELECTION);
        } else if (result === 'No') {
          updateCycleHangarId('');
          updateServiceLocationDetails(emptyServiceLocationDetails);
          updateServiceAssetDetails(null);
          if (isPhotoLocationStored && photoCoordinates) {
            const { longitude, latitude } = photoCoordinates;
            updateServiceLocationDetails({
              ...emptyServiceLocationDetails,
              longitude,
              latitude,
            });
          } else {
            updateServiceLocationDetails(emptyServiceLocationDetails);
          }

          navigate(RoutesList.ISSUE_LOCATION);
        }
        scrollTop();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            href: error.path,
          })),
        );
      }
    },
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.UPLOAD_PHOTO),
  };

  const options = [
    {
      labelText: 'Yes',
      isDefault: knowsCycleHangarId === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: knowsCycleHangarId === 'No',
    },
  ];

  return (
    <div>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>

      <h1 className="text-brand-primary body-2 text-h2 mb-8 mt-[2.75rem]">
        Cycle hangar ID details
      </h1>

      <RadioOptions
        title={radioOptionTitle}
        description={radioOptionsDescription}
        options={options}
        onChange={handleChangeBinary}
        errorPath={'knowsCycleHangarId'}
        errorMessages={errorMessages}
      />

      {result === 'Yes' ? (
        <TextInputMolecule
          label={textInputLabel}
          description={textInputDescription}
          name="cycleHangarId"
          itemID="cycleHangarId"
          disabled={false}
          value={textInput}
          type="text"
          onChange={handleChangeIdInput}
          errorPath={'cycleHangarId'}
          errorMessages={errorMessages}
        />
      ) : null}

      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </div>
  );
};
